/**
 * @file DeliveryAgreementConfirmDialog.tsx
 * @description Подтверждение отправки письма клиенту в блоке согласования
 * доставки (phase19.24 шаг 5.1, §3).
 *
 * Оба действия необратимы для клиента: повторное письмо гасит прошлую
 * ссылку, отмена гасит заявку целиком. Поэтому одно окно на все входы —
 * кнопку полосы «Сейчас», кнопку формы исхода, «Отправить повторно» и
 * «Отменить заявку» в панели ожидания.
 */

import { formatMoney } from "../../lib/api-orders";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/** Что подтверждаем: отправку письма или отмену заявки. */
export type AgreementConfirmKind = "send" | "cancel";

interface Props {
  /** `null` — окно закрыто. */
  kind: AgreementConfirmKind | null;
  /** Письмо уже уходило — предупреждаем, что старая ссылка умрёт. */
  resend: boolean;
  /** Сумма, которую увидит клиент в письме. */
  totalGross: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (kind: AgreementConfirmKind) => void;
}

export function DeliveryAgreementConfirmDialog({
  kind,
  resend,
  totalGross,
  busy,
  onClose,
  onConfirm,
}: Props) {
  const cancelling = kind === "cancel";
  return (
    <Dialog open={kind !== null} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {cancelling
              ? "Отменить заявку на согласование?"
              : resend
                ? "Отправить новое предложение?"
                : "Отправить клиенту на подтверждение?"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 text-sm">
          {cancelling ? (
            <p>
              Ссылка перестанет работать, заказ останется на согласовании —
              условия можно будет выставить заново.
            </p>
          ) : (
            <>
              <p>
                Клиент получит письмо с итоговой суммой{" "}
                <span className="font-medium">{formatMoney(totalGross)} ₽</span> и
                кнопкой подтверждения.
              </p>
              {resend ? (
                <p className="text-warning">
                  Прошлая ссылка перестанет работать — подтвердить можно будет
                  только по новому письму.
                </p>
              ) : null}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="min-h-12" disabled={busy} onClick={onClose}>
            Закрыть
          </Button>
          <Button
            className="min-h-12"
            disabled={busy}
            onClick={() => onConfirm(cancelling ? "cancel" : "send")}
          >
            {busy
              ? "Выполняем…"
              : cancelling
                ? "Отменить заявку"
                : "Отправить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
